/**
 * Brief 3.6.3 — Shared PII reveal confirm modal.
 *
 * Used wherever the admin clicks something that exposes previously-
 * hidden merchant data (phone, API credentials, future IBANs, etc.).
 * Forces a ≥20-char rationale, posts to whatever endpoint the caller
 * provides, returns the response body to the caller via onConfirmed.
 *
 * Caller is responsible for:
 *   • Providing the API path + body shape (we just inject `reason`).
 *   • Rendering the revealed value in their own surface — this modal
 *     only handles the gating, never displays the secret.
 *
 * Why a shared component: Brief 3.6.1 phone-reveal was originally
 * click-to-toggle with no reason. Brief 3.6.3 retrofitted both
 * surfaces (phone + API keys) onto the same audit shape — one modal
 * means "every PII reveal in the admin UI looks identical and
 * audit-logs identically", which is the right defence-in-depth posture.
 */
import { useEffect, useState } from "react";
import { Loader2, ShieldAlert, X } from "lucide-react";

import api from "../../lib/api";

const MIN_REASON_CHARS = 20;

export function PIIRevealConfirm({
  open,
  onClose,
  onConfirmed,
  apiPath,
  apiMethod = "POST",
  entityLabel,
  entityDescription,
  testId = "pii-reveal-confirm",
}) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Reset state every time the modal is re-opened — never carry a
  // half-typed reason across two different reveal events.
  useEffect(() => {
    if (open) {
      setReason("");
      setError("");
      setSubmitting(false);
    }
  }, [open]);

  // Esc to close
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape" && !submitting) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, submitting, onClose]);

  if (!open) return null;

  const valid = reason.trim().length >= MIN_REASON_CHARS;

  const submit = async () => {
    if (!valid) return;
    setSubmitting(true);
    setError("");
    try {
      const r = apiMethod === "GET"
        ? await api.get(apiPath)
        : await api.post(apiPath, { reason: reason.trim() });
      onConfirmed?.(r.data);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      setError(
        status === 422 ? (detail || "Reason rejected by the server.") :
        status === 410 ? (detail || "This value is no longer revealable.") :
        status === 404 ? (detail || "Nothing to reveal.") :
        "Could not reveal — please try again."
      );
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => !submitting && onClose()}
      data-testid={`${testId}-overlay`}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-white/[0.08] bg-[#0a0d12] p-6"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`Reveal ${entityLabel}`}
        data-testid={testId}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2 text-amber-300">
              <ShieldAlert className="h-4 w-4" />
              <div className="text-[10.5px] uppercase tracking-[0.18em]">PII reveal</div>
            </div>
            <div className="text-white text-[15px] mt-1">{entityLabel}</div>
            {entityDescription && (
              <div className="text-zinc-500 text-[12px] mt-1">{entityDescription}</div>
            )}
          </div>
          <button type="button" onClick={onClose} disabled={submitting}
                  data-testid={`${testId}-close`}
                  className="text-zinc-500 hover:text-white p-1 -mr-1 transition-colors disabled:opacity-40">
            <X className="h-4 w-4" />
          </button>
        </div>

        <label className="block text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">
          Reason — minimum {MIN_REASON_CHARS} characters
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={4}
          autoFocus
          maxLength={2000}
          data-testid={`${testId}-reason`}
          placeholder="Why are you revealing this? Logged for audit."
          className="w-full rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/40 focus:ring-2 focus:ring-cyan-400/20"
        />
        <div className={`mt-1 text-[11px] ${valid ? "text-zinc-500" : "text-amber-300"}`}>
          {reason.trim().length} / {MIN_REASON_CHARS} minimum
        </div>

        {error && (
          <div className="mt-3 rounded-lg border border-rose-400/30 bg-rose-400/[0.06] px-3 py-2 text-[12px] text-rose-200" data-testid={`${testId}-error`}>
            {error}
          </div>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} disabled={submitting}
                  data-testid={`${testId}-cancel`}
                  className="rounded-full border border-white/[0.08] bg-white/[0.01] px-4 py-2 text-[12.5px] text-zinc-300 hover:border-white/[0.14] hover:text-white transition-colors disabled:opacity-40">
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={!valid || submitting}
                  data-testid={`${testId}-confirm`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-400/[0.10] px-4 py-2 text-[12.5px] text-amber-100 hover:bg-amber-400/[0.16] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Reveal
          </button>
        </div>
      </div>
    </div>
  );
}
